import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';

import { ALL_FACILITIES, useFacilitiesQuery } from '@/hooks/useFacilitiesQuery';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { PostHog } from '@/lib/PostHog';
import { UserModel } from '@/models/UserModel';
import { RoutePath } from '@/routes/RoutePath';

import { getDefaultScreenerFacilityId } from './defaultScreenerView';
import { useFacilitySelectorController } from './useFacilitySelectorController';
import {
  ALL_FACILITIES_PARAM,
  allFacilitiesIntentAtom,
  FACILITY_TYPE_PARAM,
  isValidFacilityType,
  SCREENER_FACILITY_VIEW_KIND,
  SCREENER_ROUTES_WITH_ALL_FACILITIES_SUPPORT,
  SET_FACILITY_ID_PARAM,
  useScreenerFacilityView,
  useUserCanUseAllFacilities
} from './useScreenerFacilityView';
import { useWarnOnLeavingAllFacilitiesSupportedRoute } from './useWarnOnLeavingAllFacilitiesSupportedRoute';

// A reload restores history state, so the flag has to leave the entry or the
// default re-applies over the session's last selection (ENG-16699). Rewriting
// the entry in place keeps the URL and any redirect the routes below the layout
// have already queued; navigating to the captured pathname would cancel them.
const dropFirstLoginFlagFromHistory = () => {
  const { usr, ...entry } = window.history.state ?? {};
  const { isFirstLogin: _spent, ...remainingState } = usr ?? {};
  window.history.replaceState(
    {
      ...entry,
      usr: Object.keys(remainingState).length > 0 ? remainingState : null
    },
    ''
  );
};

/**
 * Side-effects backing the screener facility-selection system. Mount once at
 * the layout level (regardless of whether the FacilitySelector itself is
 * rendered) so URL params are always consumed and the warn snackbar always
 * fires. Takes the current user from the layout's guard rather than resolving
 * it again, so there is no loading state to handle here.
 */
export const useFacilitySelectorEffects = (currentUser: UserModel) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { view, selectedFacilityId, setFacilityId } = useScreenerFacilityView();
  const { selectFacility } = useFacilitySelectorController();
  const [, setIntent] = useAtom(allFacilitiesIntentAtom);
  const userCanUseAllFacilities = useUserCanUseAllFacilities();
  const { data: facilitiesResponse } =
    useFacilitiesQuery().useFindAllWithCanSelectAllFlag();
  const facilities = facilitiesResponse?.facilities;
  const isMobileViewport = useIsMobileViewport();
  const isFirstLogin = !!location.state?.isFirstLogin;
  const hasSpentFirstLoginOverride = useRef(false);

  useEffect(() => {
    const accesses = currentUser.user_facility_accesses ?? [];
    const hasValidSelection =
      (selectedFacilityId === ALL_FACILITIES && !isMobileViewport) ||
      (!!selectedFacilityId &&
        accesses.some((access) => access.facility_id === selectedFacilityId));
    const isOverridingSessionSelection =
      isFirstLogin && !hasSpentFirstLoginOverride.current;
    if (hasValidSelection && !isOverridingSessionSelection) {
      return;
    }

    if (isFirstLogin) {
      hasSpentFirstLoginOverride.current = true;
      dropFirstLoginFlagFromHistory();
    }

    const defaultFacilityType = currentUser.default_screener_facility_type;
    if (isValidFacilityType(defaultFacilityType)) {
      setIntent({ facilityType: defaultFacilityType });
    } else if (isFirstLogin) {
      setIntent(null);
    }

    const defaultFacilityId = getDefaultScreenerFacilityId(currentUser);
    if (defaultFacilityId) {
      setFacilityId(defaultFacilityId);
    }
  }, [
    selectedFacilityId,
    currentUser,
    isFirstLogin,
    setFacilityId,
    setIntent,
    isMobileViewport
  ]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const setFacilityIdParam = params.get(SET_FACILITY_ID_PARAM);
    const allFacilitiesParam = params.get(ALL_FACILITIES_PARAM);

    const isOnAllFacilitiesSupportedRoute =
      SCREENER_ROUTES_WITH_ALL_FACILITIES_SUPPORT.includes(
        location.pathname as RoutePath
      );

    const hasFacilityParams =
      !!setFacilityIdParam || allFacilitiesParam === 'true';

    if (!hasFacilityParams) {
      return;
    }

    const consumeAllFacilities =
      userCanUseAllFacilities &&
      isOnAllFacilitiesSupportedRoute &&
      allFacilitiesParam === 'true' &&
      !setFacilityIdParam;

    const consumeSetFacilityId =
      !!setFacilityIdParam && setFacilityIdParam !== selectedFacilityId;

    if (consumeSetFacilityId && setFacilityIdParam) {
      selectFacility(setFacilityIdParam);
    } else if (consumeAllFacilities) {
      const rawType = params.get(FACILITY_TYPE_PARAM);
      if (isValidFacilityType(rawType)) {
        setIntent({ facilityType: rawType });
      } else {
        setIntent(null);
      }
    }

    params.delete(SET_FACILITY_ID_PARAM);
    params.delete(ALL_FACILITIES_PARAM);
    params.delete(FACILITY_TYPE_PARAM);
    const remaining = params.toString();
    navigate(
      remaining ? `${location.pathname}?${remaining}` : location.pathname,
      { replace: true, state: location.state }
    );
  }, [
    location.pathname,
    location.search,
    location.state,
    navigate,
    selectFacility,
    selectedFacilityId,
    setIntent,
    userCanUseAllFacilities
  ]);

  useEffect(() => {
    if (view.kind === SCREENER_FACILITY_VIEW_KIND.AllFacilities) {
      PostHog.setFacility({
        scope: 'all',
        facilityId: null,
        facilityType: view.facilityType
      });
      return;
    }

    if (!selectedFacilityId || selectedFacilityId === ALL_FACILITIES) {
      PostHog.setFacility({
        scope: 'single',
        facilityId: null,
        facilityType: null
      });
      return;
    }

    const facility = facilities?.find((f) => f.id === selectedFacilityId);
    PostHog.setFacility({
      scope: 'single',
      facilityId: selectedFacilityId,
      facilityType: facility?.facility_type ?? null
    });
  }, [view.kind, view.facilityType, selectedFacilityId, facilities]);

  useWarnOnLeavingAllFacilitiesSupportedRoute({
    pathname: location.pathname,
    isInAllFacilitiesView: view.kind === 'all-facilities',
    selectedFacilityType: view.facilityType
  });
};
